import { socialLinks, googleBusinessProfileUrl, businessLocation, businessMapUrl } from "@/config/navigation";

const SITE_URL = "https://coreshift.co.za";
const SITE_NAME = "Coreshift";
const SITE_PHONE = "076 041 6913";

/**
 * Social links minus any facebook.com/share/ URL — a share-action link, not
 * the Page's permanent identity, so it must never be published as schema
 * sameAs (Google/Facebook treat it as ephemeral, unlike a Page URL).
 */
function schemaSafeSameAs(extra: string[] = []) {
  return [
    ...socialLinks.map((link) => link.href).filter((href) => !href.includes("facebook.com/share/")),
    ...extra,
  ];
}

export interface Crumb {
  label: string;
  /** Route path, e.g. "/contact". Omit for the current (last) page. */
  path?: string;
}

export function breadcrumbSchema(items: Crumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    // Per Google's guidance, the last item (the current page) can omit
    // `item` entirely rather than provide a URL — previously this defaulted
    // to the bare site root for every page's own crumb, since `path` is
    // intentionally left unset for the current (non-linked) page.
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.label,
      ...(item.path ? { item: `${SITE_URL}${item.path}` } : {}),
    })),
  };
}

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.png`,
    sameAs: schemaSafeSameAs(),
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
  };
}

/**
 * NAP-consistent ProfessionalService schema — name/telephone/url must match
 * the footer and Contact page exactly. areaServed is deliberately two-tier
 * (city for local relevance, country for real national/international reach)
 * since schema isn't policed the way GBP's service-area field is.
 */
export function professionalServiceSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: SITE_NAME,
    telephone: SITE_PHONE,
    url: SITE_URL,
    image: `${SITE_URL}/favicon.png`,
    address: {
      "@type": "PostalAddress",
      addressLocality: businessLocation.city,
      addressRegion: businessLocation.region,
      addressCountry: businessLocation.countryCode,
    },
    ...(businessLocation.geo
      ? {
          geo: {
            "@type": "GeoCoordinates",
            latitude: businessLocation.geo.latitude,
            longitude: businessLocation.geo.longitude,
          },
        }
      : {}),
    hasMap: businessMapUrl,
    description:
      "Coreshift builds custom website design, business management systems, booking platforms, inventory management tools, and AI-driven workflow automation. We work with growing and ambitious businesses across Gqeberha, the Eastern Cape, and clients throughout South Africa and internationally.",
    areaServed: [
      {
        "@type": "City",
        name: "Gqeberha",
        alternateName: ["Port Elizabeth", "Nelson Mandela Bay"],
        containedInPlace: {
          "@type": "Country",
          name: "South Africa",
        },
      },
      {
        "@type": "Country",
        name: "South Africa",
      },
    ],
    sameAs: schemaSafeSameAs([googleBusinessProfileUrl]),
    // Matches the hours published on the Google Business Profile listed
    // above (Mon-Fri 8am-5pm, closed weekends) — keep these in sync if
    // that listing's hours ever change.
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "17:00",
      },
    ],
    serviceType: [
      "Web Design & Development",
      "Integrations & Automation",
      "Product Engineering",
      "Signage & Vinyl Printing Services",
      "Web Hosting & Domain Hosting",
      "Ongoing Support",
    ],
  };
}
